import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, ArrowUpRight } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { BRANDS } from "@/data/brands";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Only allow http(s) URLs to be rendered as-is; anything else falls back to
// the internal booking anchor — protects against a rogue admin planting a
// `javascript:` URL. (Server also enforces this via Pydantic pattern.)
const safeUrl = (u) =>
  typeof u === "string" && /^https?:\/\//i.test(u) ? u : null;

// Live trade-shows section — data comes from GET /api/trade-shows.
// Admins can add/edit/delete rows from the admin dashboard.
// -----------------------------------------------------------------------------

export default function TradeShows() {
  const { t } = useT();

  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/trade-shows`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!cancelled) setShows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setErrored(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Hide the whole section if the DB is empty and we're not loading —
  // avoids showing an empty stub. Keep visible during load for LCP stability.
  if (!loading && (errored || shows.length === 0)) return null;

  return (
    <section
      id="trade-shows"
      className="relative py-24 lg:py-32 overflow-hidden bg-[#001F39] text-white"
      data-testid="trade-shows"
    >
      <img
        aria-hidden
        src="/hupla-emblem.svg"
        alt=""
        className="pointer-events-none select-none absolute -right-40 top-1/2 -translate-y-1/2 w-[720px] max-w-none opacity-[0.06]"
        style={{ filter: "brightness(0) invert(1)" }}
      />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
          <div className="lg:col-span-5">
            <p className="text-[0.7rem] md:text-xs font-bold uppercase mb-4 text-[#E8B14B] tracking-[0.36em]">
              {t("shows.kicker")}
            </p>
            <h2 className="font-editorial italic leading-[1.05] text-[clamp(2rem,3.8vw,3.25rem)]">
              {t("shows.title")}
            </h2>
            <div className="mt-6 h-px w-24 bg-gradient-to-r from-[#E8B14B] to-transparent" />
          </div>
          <div className="lg:col-span-7 flex items-end">
            <p className="text-base md:text-lg leading-[1.75] text-white/75 max-w-xl">
              {t("shows.body")}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-5 lg:gap-6" aria-busy="true">
            {[0, 1, 2].map((k) => (
              <div key={k} className="animate-pulse rounded-2xl bg-white/[0.04] h-72" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
            {shows.map((show, i) => (
              <motion.article
                key={show.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="relative overflow-hidden rounded-2xl p-7 lg:p-8 flex flex-col text-[#001F39]"
                style={{
                  background: "#FBF6EC",
                  borderTop: `4px solid ${show.accent}`,
                }}
                data-testid={`trade-show-${show.id}`}
              >
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-3" style={{ color: show.accent }}>
                  <Calendar className="w-3.5 h-3.5" />
                  {show.date_label}
                </div>

                <h3 className="font-editorial italic leading-[1.05] text-3xl md:text-4xl mb-1">
                  {show.name}
                </h3>
                <p className="text-sm text-[#001F39]/70 mb-6">
                  {show.city}, {show.country}
                </p>

                <div className="space-y-3 text-sm mb-6 flex-1">
                  <p className="flex items-start gap-2 text-[#001F39]/80">
                    <MapPin className="w-4 h-4 mt-[3px] flex-shrink-0" style={{ color: show.accent }} />
                    <span>
                      <span className="block">{show.venue}</span>
                      <span className="block text-[#001F39]/55">{show.hall}</span>
                    </span>
                  </p>
                </div>

                <div>
                  <p className="text-[0.6rem] font-bold uppercase tracking-[0.28em] text-[#001F39]/55 mb-2">
                    {t("shows.showcasing")}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    {BRANDS.map((brand) => (
                      <img
                        key={brand.slug}
                        src={brand.logo}
                        alt={brand.name}
                        title={brand.name}
                        className="h-6 md:h-7 w-auto object-contain"
                        loading="lazy"
                      />
                    ))}
                  </div>
                </div>

                <a
                  href={safeUrl(show.url) || "/#b2b"}
                  target={safeUrl(show.url) ? "_blank" : undefined}
                  rel={safeUrl(show.url) ? "noopener noreferrer" : undefined}
                  data-testid={`trade-show-book-${show.id}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold hover:opacity-80 transition-opacity"
                  style={{ color: show.accent }}
                >
                  {t("shows.book")}
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
